import { memo, useMemo } from 'react'
import { Stack, TableCell, Typography } from '@mui/material'
import { useSelector } from 'react-redux'
import { Security } from '../../proto/generated/security'
import { Watchlist_WatchlistItem } from '../../proto/generated/watchlist'
import { selectCustomizedColumns } from '../../selectors/user'
import LiveMarketText from '../common/LiveMarketText'
import { buildWatchlistLiveSelectors } from './watchlistLiveDisplay'

const FONT_FEATURE_NUM = { fontFeatureSettings: '"tnum" on,"lnum" on' } as const

type WatchlistItemPricesProps = {
    item: Watchlist_WatchlistItem
    security?: Security
}

export const WatchlistNormalItemPrices = memo(function WatchlistNormalItemPrices({
    item,
    security,
}: WatchlistItemPricesProps) {
    const selectors = useMemo(
        () => buildWatchlistLiveSelectors(item, security, 'locale'),
        [item, item.type, item.security, item.securityExpression, security]
    )

    if (!selectors) {
        return null
    }

    return (
        <Stack direction="column" alignItems="flex-end" justifyContent="flex-end" flexGrow={1} spacing={0}>
            <Typography
                variant="body2"
                align="right"
                color="textSecondary"
                component="div"
                style={{ width: 70, ...FONT_FEATURE_NUM }}
            >
                <LiveMarketText className="font-feature-num" selectText={selectors.ltp} />
            </Typography>
            <Stack direction="row" spacing={0} alignItems="flex-end" component="div">
                <Typography variant="body2" component="span" sx={{ opacity: 0.7 }} className="font-feature-num">
                    <LiveMarketText
                        selectText={selectors.ltpChange}
                        selectColor={selectors.changeColor}
                    />
                </Typography>
                <Typography
                    variant="body2"
                    color="textSecondary"
                    align="right"
                    component="span"
                    sx={{ width: 55, ...FONT_FEATURE_NUM }}
                >
                    <LiveMarketText
                        selectText={selectors.changePercent}
                        selectColor={selectors.changeColor}
                    />
                </Typography>
            </Stack>
        </Stack>
    )
})

export const WatchlistTableItemPrices = memo(function WatchlistTableItemPrices({
    item,
    security,
}: WatchlistItemPricesProps) {
    const customizedColumns = useSelector(selectCustomizedColumns)
    const selectors = useMemo(
        () => buildWatchlistLiveSelectors(item, security, 'fixed2'),
        [item, item.type, item.security, item.securityExpression, security]
    )

    if (!selectors) {
        return null
    }

    const cellTypographySx = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        ...FONT_FEATURE_NUM,
    } as const

    return (
        <>
            {customizedColumns.last && (
                <TableCell style={{ padding: 0, boxSizing: 'border-box' }}>
                    <Typography variant="body2" align="right" component="div" sx={cellTypographySx}>
                        <LiveMarketText selectText={selectors.ltp} />
                    </Typography>
                </TableCell>
            )}
            {customizedColumns.change && (
                <TableCell style={{ boxSizing: 'border-box', padding: 0 }}>
                    <Typography variant="body2" align="right" component="div" sx={cellTypographySx}>
                        <LiveMarketText
                            selectText={selectors.ltpChange}
                            selectColor={selectors.changeColor}
                        />
                    </Typography>
                </TableCell>
            )}
            {customizedColumns.changePercentage && (
                <TableCell sx={{ padding: 0, boxSizing: 'border-box' }}>
                    <Typography variant="body2" align="right" component="div" sx={cellTypographySx}>
                        <LiveMarketText
                            selectText={selectors.changePercent}
                            selectColor={selectors.changeColor}
                        />
                    </Typography>
                </TableCell>
            )}
            {customizedColumns.volume && (
                <TableCell sx={{ boxSizing: 'border-box', p: 0, paddingRight: '20px' }}>
                    <Typography
                        variant="body2"
                        component="div"
                        sx={{
                            ...cellTypographySx,
                            opacity: 0.7,
                            color: 'text.secondary',
                        }}
                    >
                        <LiveMarketText selectText={selectors.volume} />
                    </Typography>
                </TableCell>
            )}
        </>
    )
})
